import { resolveGuestFacingOrderHref } from "@/lib/features";
import {
  getLocationBySlug,
  romaBranchLocations,
  type PlatformOrderingConfig,
  type RomaBranchLocation,
} from "@/lib/restaurant-platform";

export const ROMA_SOUTHERN_HIGHLANDS_LOCATION_ID =
  "roma-kitchen-southern-highlands" as const;
export const ROMA_HENDERSON_BRANCH_ID = "roma-kitchen-henderson" as const;

export type RomaOrderLocationSlug = "southern-highlands" | "henderson";

export type RomaOrderProvider = "first_party" | "grubhub";

export type RomaOrderDestination = {
  locationSlug: RomaOrderLocationSlug;
  locationId: string;
  cityLabel: string;
  chooserLabel: string;
  ctaLabel: string;
  href: string;
  mode: PlatformOrderingConfig["mode"];
  provider: RomaOrderProvider;
  opensInNewTab: boolean;
};

export type RomaGenericOrderBehavior =
  | { kind: "direct"; destination: RomaOrderDestination }
  | { kind: "chooser"; options: RomaOrderDestination[] };

const HENDERSON_SCOPED_PATHS = [
  "/best/italian-restaurant-henderson-nv",
  "/best/catering-henderson-nv",
  "/best/private-dining-henderson-las-vegas",
  "/gaming",
  "/blog/best-italian-restaurant-henderson-nv",
] as const;

const SOUTHERN_HIGHLANDS_SCOPED_PATHS = [
  "/best/italian-restaurant-southern-highlands-las-vegas",
] as const;

function normalizePathname(pathname: string): string {
  const trimmed = pathname.trim();
  if (trimmed.length === 0) return "/";
  const withLeading = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  if (withLeading.length > 1 && withLeading.endsWith("/")) {
    return withLeading.slice(0, -1);
  }
  return withLeading;
}

function pathMatches(pathname: string, prefixes: readonly string[]): boolean {
  const path = normalizePathname(pathname);
  return prefixes.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

export function getRomaHendersonBranch(): RomaBranchLocation {
  const branch = romaBranchLocations.find(
    (candidate) => candidate.id === ROMA_HENDERSON_BRANCH_ID,
  );
  if (!branch) {
    throw new Error("Roma Henderson branch configuration is required");
  }
  return branch;
}

export function getRomaHendersonOrderHref(): string {
  const href = getRomaHendersonBranch().ordering?.orderUrl?.trim() ?? "";
  if (!href) {
    throw new Error("Roma Henderson ordering.orderUrl is required");
  }
  return href;
}

export function getRomaLocationOrderDestination(
  slug: RomaOrderLocationSlug,
): RomaOrderDestination {
  if (slug === "henderson") {
    const href = getRomaHendersonOrderHref();
    const provider =
      getRomaHendersonBranch().ordering?.provider ?? "grubhub";
    return {
      locationSlug: "henderson",
      locationId: ROMA_HENDERSON_BRANCH_ID,
      cityLabel: "Henderson",
      chooserLabel: "Henderson — Order on Grubhub",
      ctaLabel: "Order on Grubhub",
      href,
      mode: "external",
      provider,
      opensInNewTab: true,
    };
  }

  const location = getLocationBySlug(ROMA_SOUTHERN_HIGHLANDS_LOCATION_ID);
  const href = resolveGuestFacingOrderHref({
    orderingMode: location.ordering.mode,
    orderUrl: location.orderUrl,
    guestOrderCta: location.guestOrderCta,
  });
  const provider = location.guestOrderCta?.provider ?? "first_party";
  const grubhubLabels = provider === "grubhub";

  return {
    locationSlug: "southern-highlands",
    locationId: ROMA_SOUTHERN_HIGHLANDS_LOCATION_ID,
    cityLabel: "Southern Highlands",
    chooserLabel: grubhubLabels
      ? "Southern Highlands — Order on Grubhub"
      : "Southern Highlands — Order pickup",
    ctaLabel: grubhubLabels ? "Order on Grubhub" : "Order pickup",
    href,
    // Keep first-party mode even when guest CTAs temporarily go to Grubhub.
    mode: location.ordering.mode,
    provider,
    opensInNewTab: href.startsWith("http"),
  };
}

export function listRomaOrderChooserOptions(): RomaOrderDestination[] {
  return [
    getRomaLocationOrderDestination("southern-highlands"),
    getRomaLocationOrderDestination("henderson"),
  ];
}

export function resolveRomaScopedOrderDestination(
  pathname: string,
): RomaOrderDestination | null {
  if (pathMatches(pathname, HENDERSON_SCOPED_PATHS)) {
    return getRomaLocationOrderDestination("henderson");
  }
  if (pathMatches(pathname, SOUTHERN_HIGHLANDS_SCOPED_PATHS)) {
    return getRomaLocationOrderDestination("southern-highlands");
  }
  return null;
}

export function resolveRomaGenericOrderBehavior(
  pathname: string,
): RomaGenericOrderBehavior {
  const scoped = resolveRomaScopedOrderDestination(pathname);
  if (scoped) {
    return { kind: "direct", destination: scoped };
  }
  return { kind: "chooser", options: listRomaOrderChooserOptions() };
}
